import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Text, Badge, Button, HStack, VStack, Flex, Divider, useToast,
  Input, Textarea, FormControl, FormLabel, Select, SimpleGrid,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, IconButton, useDisclosure, Tag, TagLabel, TagCloseButton,
  Wrap, Spinner,
} from '@chakra-ui/react';
import { FiPlus, FiRefreshCw, FiTrash2, FiEdit2, FiUser, FiSearch } from 'react-icons/fi';
import api from '../api';

const STATUS_COLOR = { active:'blue', converted:'green', lost:'red', inactive:'gray' };
const fmt = d => d ? new Date(d.split('T')[0]+'T12:00:00').toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';

// Contact search component
function ContactSearch({ onSelect, placeholder = 'Search contacts...' }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/contacts/search?q=${encodeURIComponent(val)}`);
      setResults(data || []);
    } catch(e) { setResults([]); }
    setLoading(false);
  };

  return (
    <Box position="relative">
      <Input size="sm" placeholder={placeholder} value={q} onChange={e => search(e.target.value)} />
      {results.length > 0 && (
        <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px"
          borderColor="gray.200" borderRadius="md" shadow="md" zIndex={20} maxH="180px" overflowY="auto">
          {results.map(c => (
            <Box key={c.id} px={3} py={2} cursor="pointer" fontSize="12px" _hover={{ bg:'blue.50' }}
              onClick={() => { onSelect(c); setQ(`${c.first_name} ${c.last_name||''}`); setResults([]); }}>
              <Text fontWeight="600">{c.first_name} {c.last_name}</Text>
              {c.company_name && <Text fontSize="10px" color="gray.400">{c.company_name}</Text>}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// Project search by name
function ProjectSearch({ onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);

  const search = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults([]); return; }
    try {
      const { data } = await api.get(`/projects/search?q=${encodeURIComponent(val)}&full=1&limit=20`);
      setResults(Array.isArray(data) ? data : []);
    } catch(e) { setResults([]); }
  };

  return (
    <Box position="relative">
      <Input size="sm" placeholder="Search projects..." value={q} onChange={e => search(e.target.value)} />
      {results.length > 0 && (
        <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px"
          borderColor="gray.200" borderRadius="md" shadow="md" zIndex={20} maxH="200px" overflowY="auto">
          {results.map(p => (
            <Box key={p.id} px={3} py={2} cursor="pointer" fontSize="12px" _hover={{ bg:'blue.50' }}
              onClick={() => { onSelect(p); setQ(p.project_name); setResults([]); }}>
              <Text fontWeight="600">{p.project_name}</Text>
              {p.company_name && <Text fontSize="10px" color="gray.400">{p.company_name}</Text>}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function AddReferralModal({ isOpen, onClose, onSaved }) {
  const [contact, setContact] = useState(null);
  const [project, setProject] = useState(null);
  const [searchMode, setSearchMode] = useState('project'); // 'project' | 'company'
  const [companySearch, setCompanySearch] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyProjects, setCompanyProjects] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const reset = () => {
    setContact(null); setProject(null); setNotes('');
    setCompanySearch(''); setCompanySuggestions([]); setSelectedCompany(null); setCompanyProjects([]);
  };

  useEffect(() => { if (!isOpen) reset(); }, [isOpen]); // eslint-disable-line

  const searchCompanies = async (q) => {
    setCompanySearch(q);
    if (q.length < 2) { setCompanySuggestions([]); return; }
    try {
      const { data } = await api.get(`/companies/search?q=${encodeURIComponent(q)}`);
      setCompanySuggestions(data || []);
    } catch(e) { setCompanySuggestions([]); }
  };

  const selectCompany = async (co) => {
    setSelectedCompany(co); setCompanySearch(co.name); setCompanySuggestions([]);
    try {
      const { data } = await api.get(`/projects/by-company-name?name=${encodeURIComponent(co.name)}`);
      setCompanyProjects(data || []);
    } catch(e) { setCompanyProjects([]); }
  };

  const save = async () => {
    if (!contact) { toast({ title:'Please select a referrer contact', status:'warning', duration:2000 }); return; }
    if (!project) { toast({ title:'Please select a project', status:'warning', duration:2000 }); return; }
    setSaving(true);
    try {
      await api.post('/referrals', {
        contact_id: contact.id,
        project_id: project.id,
        notes: notes || null,
        referred_date: new Date().toISOString().split('T')[0],
      });
      toast({ title:'Referral recorded', status:'success', duration:2000 });
      onSaved(); onClose();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status:'error', duration:2000 }); }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay /><ModalContent>
        <ModalHeader fontSize="md">Record a Referral</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Referrer contact */}
            <FormControl isRequired>
              <FormLabel fontSize="xs">Referred By (Contact) *</FormLabel>
              <ContactSearch onSelect={setContact} placeholder="Search all contacts..." />
              {contact && (
                <HStack mt={1}>
                  <Badge colorScheme="blue">{contact.first_name} {contact.last_name} {contact.company_name ? `— ${contact.company_name}` : ''}</Badge>
                  <Button size="xs" variant="ghost" colorScheme="red" onClick={() => setContact(null)}>Clear</Button>
                </HStack>
              )}
            </FormControl>

            {/* Project selection */}
            <FormControl isRequired>
              <FormLabel fontSize="xs">Project Being Referred *</FormLabel>
              <HStack mb={2} spacing={1}>
                <Button size="xs" variant={searchMode==='project'?'solid':'outline'} colorScheme="blue"
                  onClick={() => { setSearchMode('project'); setProject(null); setSelectedCompany(null); }}>📁 By Project Name</Button>
                <Button size="xs" variant={searchMode==='company'?'solid':'outline'} colorScheme="blue"
                  onClick={() => { setSearchMode('company'); setProject(null); }}>🏢 By Company</Button>
              </HStack>

              {searchMode === 'project' && (
                <>
                  <ProjectSearch onSelect={setProject} />
                  {project && <Badge colorScheme="purple" mt={1}>{project.project_name}</Badge>}
                </>
              )}

              {searchMode === 'company' && (
                <Box>
                  {!selectedCompany ? (
                    <Box position="relative">
                      <Input size="sm" placeholder="Search company..." value={companySearch}
                        onChange={e => searchCompanies(e.target.value)} />
                      {companySuggestions.length > 0 && (
                        <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px"
                          borderColor="blue.200" borderRadius="md" shadow="md" zIndex={20} maxH="160px" overflowY="auto">
                          {companySuggestions.map(co => (
                            <Box key={co.id} px={3} py={2} cursor="pointer" fontSize="12px"
                              _hover={{bg:'blue.50'}} onClick={() => selectCompany(co)}>
                              <Text fontWeight="600">{co.name}</Text>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <HStack mb={2}>
                        <Badge colorScheme="blue">{selectedCompany.name}</Badge>
                        <Button size="xs" variant="ghost" colorScheme="red" onClick={() => { setSelectedCompany(null); setCompanySearch(''); setCompanyProjects([]); setProject(null); }}>Change</Button>
                      </HStack>
                      {companyProjects.length === 0
                        ? <Text fontSize="12px" color="gray.400">No projects found for this company.</Text>
                        : <Select size="sm" placeholder="Select project..." onChange={e => {
                            const p = companyProjects.find(x => x.id === e.target.value);
                            if (p) setProject(p);
                          }}>
                            {companyProjects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                          </Select>
                      }
                      {project && <Badge colorScheme="purple" mt={1}>{project.project_name}</Badge>}
                    </Box>
                  )}
                </Box>
              )}
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs">Notes (optional)</FormLabel>
              <Textarea size="sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="How did this referral come about?" />
            </FormControl>

            <Box bg="blue.50" p={2} borderRadius="md" fontSize="11px" color="blue.700">
              This referral will automatically be added to the <strong>Referrals</strong> prospecting campaign.
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>Record Referral</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function ReferralsDashboard({ isOpen, onClose, onBack, fullPage }) {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const addDisc = useDisclosure();
  const toast = useToast();

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/referrals'); setReferrals(data || []); } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { if (isOpen) fetch(); }, [isOpen, fetch]);

  const deleteRef = async (id) => {
    try {
      await api.delete(`/referrals/${id}`);
      toast({ title: 'Referral removed', status: 'info', duration: 1500 });
      fetch();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const filtered = referrals.filter(r => statusFilter === 'all' || r.status === statusFilter);
  const counts = { active: referrals.filter(r=>r.status==='active').length, converted: referrals.filter(r=>r.status==='converted').length, lost: referrals.filter(r=>r.status==='lost').length };

  if (!isOpen && !fullPage) return null;

  return (
    <>
    <Box h="100%" overflowY="auto" p={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <HStack>
          {onBack && <Button size="sm" variant="ghost" onClick={onBack}>← Back</Button>}
          <Text fontSize="lg" fontWeight="700">Referrals</Text>
        </HStack>
        <HStack>
          <IconButton icon={<FiRefreshCw size={13}/>} size="sm" variant="ghost" onClick={fetch} aria-label="Refresh"/>
          <Button size="sm" colorScheme="blue" leftIcon={<FiPlus size={13}/>} onClick={addDisc.onOpen}>Record Referral</Button>
        </HStack>
      </Flex>

      {/* Summary */}
      <SimpleGrid columns={4} spacing={3} mb={4}>
        <Box bg="blue.50" p={3} borderRadius="md" textAlign="center">
          <Text fontSize="22px" fontWeight="800" color="blue.600">{referrals.length}</Text>
          <Text fontSize="10px" color="gray.500">Total Referrals</Text>
        </Box>
        <Box bg="blue.50" p={3} borderRadius="md" textAlign="center">
          <Text fontSize="22px" fontWeight="800" color="blue.500">{counts.active}</Text>
          <Text fontSize="10px" color="gray.500">Active</Text>
        </Box>
        <Box bg="green.50" p={3} borderRadius="md" textAlign="center">
          <Text fontSize="22px" fontWeight="800" color="green.600">{counts.converted}</Text>
          <Text fontSize="10px" color="gray.500">Converted</Text>
        </Box>
        <Box bg="red.50" p={3} borderRadius="md" textAlign="center">
          <Text fontSize="22px" fontWeight="800" color="red.500">{counts.lost}</Text>
          <Text fontSize="10px" color="gray.500">Lost</Text>
        </Box>
      </SimpleGrid>

      {/* Status filter */}
      <HStack spacing={1} mb={3} flexWrap="wrap">
        {['all','active','converted','lost','inactive'].map(s => (
          <Badge key={s} cursor="pointer" fontSize="11px" px={2} py={1}
            colorScheme={statusFilter===s ? (s==='all'?'blue':STATUS_COLOR[s]) : 'gray'}
            variant={statusFilter===s ? 'solid' : 'outline'}
            onClick={() => setStatusFilter(s)}
            textTransform="capitalize">{s==='all' ? `All (${referrals.length})` : `${s} (${referrals.filter(r=>r.status===s).length})`}</Badge>
        ))}
      </HStack>

      {loading ? <Flex justify="center" py={12}><Spinner /></Flex> : filtered.length === 0 ? (
        <Text color="gray.400" textAlign="center" py={12}>No referrals yet. Record your first referral to start tracking.</Text>
      ) : (
        <VStack spacing={2} align="stretch">
          {filtered.map(r => (
            <Box key={r.id} p={3} border="1px" borderColor="blue.100" borderRadius="md" bg="white">
              <Flex justify="space-between" align="flex-start">
                <Box flex={1}>
                  <HStack spacing={2} mb={1} flexWrap="wrap">
                    <Badge colorScheme={STATUS_COLOR[r.status]||'gray'} fontSize="8px" textTransform="capitalize">{r.status}</Badge>
                    <Text fontSize="13px" fontWeight="700">
                      <FiUser size={11} style={{display:'inline',marginRight:3}} />{r.referrer_name?.trim()}
                      {r.referrer_company && <Text as="span" fontSize="11px" color="gray.500" fontWeight="400"> — {r.referrer_company}</Text>}
                    </Text>
                  </HStack>
                  <HStack spacing={3} fontSize="11px" color="gray.600" flexWrap="wrap">
                    {r.project_name && <Text>📁 {r.project_name}</Text>}
                    {r.campaign_name && <Badge colorScheme="purple" fontSize="8px">{r.campaign_name}</Badge>}
                    <Text color="gray.400">📅 {fmt(r.referred_date)}</Text>
                  </HStack>
                  {r.notes && <Text fontSize="10px" color="gray.500" mt={1} noOfLines={2}>{r.notes}</Text>}
                </Box>
                <HStack spacing={1} ml={2}>
                  <Select size="xs" value={r.status} w="110px"
                    onChange={async e => {
                      await api.put(`/referrals/${r.id}`, { status: e.target.value });
                      fetch();
                    }}>
                    <option value="active">Active</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                    <option value="inactive">Inactive</option>
                  </Select>
                  <IconButton size="xs" icon={<FiTrash2 size={11}/>} variant="ghost" colorScheme="red"
                    onClick={() => deleteRef(r.id)} aria-label="Delete" />
                </HStack>
              </Flex>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
    <AddReferralModal isOpen={addDisc.isOpen} onClose={addDisc.onClose} onSaved={fetch} />
    </>
  );
}
